import React, { Dispatch, Fragment, memo, useCallback, useEffect, useState } from 'react';

import { connect } from 'react-redux';
import PropTypes from 'prop-types';

import HelpIcon from '@mui/icons-material/Help';

import { IconButton, ListItem, ListItemIcon, ListItemText, Theme, Tooltip } from '@mui/material';
import { withStyles } from '@mui/styles';

import { useHelpLangFile, useLangFile } from 'js/context/LanguageContext';
import MarkdownArticleDialog from 'js/components/MarkdownArticleDialog/MarkdownArticleDialog';
import { setHelpArticleSeen } from 'js/reducers/HelpReducer';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import { voidFunc } from '../../constants/PropTypeUtils';

const mapStateToProps = (store) => {
  return {
    seenHelpArticleIDs: store.help.seenHelpArticleIDs,
    language: store.language.language,
    showWeatherNetwork: store.weatherNetwork.showWeatherNetwork,
  };
};

const styles = (theme: Theme) => ({
  iconRoot: {
    minWidth: 36,
  },
});

type HelpItemContainerProps = {
  dispatch: Dispatch<any>;
  classes: {
    iconRoot: string;
  };
  seenHelpArticleIDs: string[];
  language: string;
  showWeatherNetwork: boolean;
  articleId: string;
  type?: 'button' | 'listitem' | 'icon' | 'overlay-icon';
  onShow?: () => void;
  onClose?: () => void;
};

const HelpItemContainer: React.FC<HelpItemContainerProps> = ({
  dispatch,
  classes,
  seenHelpArticleIDs,
  articleId,
  type = 'listitem',
  onClose = () => {},
  onShow = () => {},
}) => {
  const LangFile = useLangFile();
  const [showArticle, setShowArticle] = useState(false);
  const helpArticles = useHelpLangFile();
  const article = helpArticles[articleId];
  const seen = article && seenHelpArticleIDs.indexOf(article.id) !== -1;

  const handleOnClick = useCallback(() => setShowArticle(true), []);

  useEffect(() => {
    if (article) {
      if (!seen) {
        setShowArticle(true);
        onShow();
      }
    }
  }, [article, seen, onShow]);

  const handleOnClose = useCallback(
    (a) => {
      if (article && !seen) {
        dispatch(setHelpArticleSeen(a.id));
      }
      setShowArticle(false);
      onClose();
    },
    [article, seen, onClose]
  );

  if (!article) {
    return null;
  }

  let item: {};
  if (type === 'button') {
    item = (
      <Button variant={'contained'} onClick={handleOnClick}>
        <HelpIcon color="primary" fontSize={'small'} />
        <Box
          sx={{
            color: 'primary.main',
            ml: 0.5,
          }}
        >
          {LangFile.HelpItemContainer.primary}
        </Box>
      </Button>
    );
  } else if (type === 'icon') {
    item = (
      <ListItem
        style={{ position: 'fixed', left: '90%', top: '3%', width: 'auto' }}
        dense
        button
        disableGutters
        onClick={handleOnClick}
      >
        <ListItemIcon style={{ minWidth: 0 }}>
          <HelpIcon color="primary" fontSize={'small'} />
        </ListItemIcon>
      </ListItem>
    );
  } else if (type === 'overlay-icon') {
    item = (
      <Tooltip title={article.title}>
        <IconButton
          color="inherit"
          size="small"
          sx={{
            position: 'absolute',
            right: 0,
            bottom: 6,
            width: 24,
            height: 24,
            p: 0,
            bgcolor: 'background.paper',
            zIndex: 1,
          }}
          aria-label={`${LangFile.HelpItemContainer.primary}: ${article.title}`}
          onClick={handleOnClick}
        >
          <HelpIcon sx={{ fontSize: 17 }} />
        </IconButton>
      </Tooltip>
    );
  } else {
    item = (
      <ListItem dense button divider onClick={handleOnClick}>
        <ListItemIcon classes={{ root: classes.iconRoot }}>
          <HelpIcon color="primary" fontSize={'small'} />
        </ListItemIcon>
        <ListItemText primary={LangFile.HelpItemContainer.primary} />
      </ListItem>
    );
  }

  return (
    <Fragment>
      {item}

      {showArticle && (
        <MarkdownArticleDialog
          LangFile={LangFile}
          article={article}
          onClose={handleOnClose}
          key={article.id}
        />
      )}
    </Fragment>
  );
};

HelpItemContainer.propTypes = {
  articleId: PropTypes.string.isRequired,
  type: PropTypes.oneOf(['button', 'listitem', 'icon', 'overlay-icon']),
  onShow: PropTypes.func,
  onClose: PropTypes.func,
};

HelpItemContainer.defaultProps = {
  type: 'listitem',
  onShow: voidFunc,
  onClose: voidFunc,
};

export default memo(withStyles(styles)(connect(mapStateToProps)(HelpItemContainer)));
